<script setup lang="ts">
import { computed, ref } from 'vue'
import { deleteServicePlanAction } from '@/api/functions/services/service-plan.action'
import type { ServicePlan } from '@/api/typings/service.typing.ts'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import CreatePlanModal from './CreatePlanModal.vue'
import UpdatePlanModal from './UpdatePlanModal.vue'

const props = defineProps<{
  plans: ServicePlan[]
  serviceSlug: string | undefined
  countries: {
    label: string
    value: string
    flagUrl: string
    iso: string
  }[]
  currencies: {
    id: string
    isoCode: string
  }[]
}>()

const emit = defineEmits<(e: 'refetch') => void>()

const toast = useToast()

const showDeletePlanModal = ref(false)
const planToDelete = ref<ServicePlan | null>(null)
const showCreatePlanModal = ref(false)
const showUpdatePlanModal = ref(false)
const selectedPlan = ref<ServicePlan | null>(null)

const planColumns = [
  {
    key: 'index',
    label: '#',
    class: 'col-span-1 font-fa text-sm',
  },
  {
    key: 'name',
    label: 'نام پلن',
    class: 'col-span-2 text-sm',
  },
  {
    key: 'slug',
    label: 'اسلاگ',
    class: 'col-span-2 font-fa text-sm',
  },
  {
    key: 'duration',
    label: 'مدت اعتبار',
    class: 'col-span-2 font-fa text-sm',
  },
  {
    key: 'price',
    label: 'قیمت',
    class: 'col-span-2 font-fa text-sm',
  },
  {
    key: 'status',
    label: 'وضعیت',
    class: 'col-span-1 text-sm',
  },
]

const planRows = computed(() => {
  return props.plans.map((plan, index) => ({
    ...plan,
    index: index + 1,
    duration: plan.duration_days ? `${plan.duration_days} روز` : '—',
    price: plan.prices.map((price) => `${price.amount} ${price?.currency?.iso_code ?? ''}`).join(' / ') || '—',
    status: plan.is_active ? 'فعال' : 'غیرفعال',
  }))
})

const planActions = [
  {
    label: 'ویرایش',
    handlerName: 'editPlan',
  },
  {
    label: 'حذف',
    handlerName: 'deletePlan',
  },
]

const deletePlanMutation = useMutationApi({
  mutationFn: () => {
    if (!props.serviceSlug) {
      throw new Error('شناسه سرویس معتبر نیست')
    }

    if (!planToDelete.value?.id) {
      throw new Error('پلن معتبری برای حذف انتخاب نشده است')
    }

    return deleteServicePlanAction(props.serviceSlug, planToDelete.value.id)
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const isDeleting = computed(() => deletePlanMutation.isPending.value)

const onCreatePlan = () => {
  showCreatePlanModal.value = true
}

const handlePlanCreate = () => {
  emit('refetch')
}

const onEditPlan = (plan: ServicePlan) => {
  selectedPlan.value = plan
  showUpdatePlanModal.value = true
}

const handlePlanUpdate = () => {
  showUpdatePlanModal.value = false
  selectedPlan.value = null
  emit('refetch')
}

const onDeletePlan = (plan: ServicePlan) => {
  planToDelete.value = plan
  showDeletePlanModal.value = true
}

const handleDeletePlan = async () => {
  if (isDeleting.value) return

  try {
    await deletePlanMutation.mutateAsync()

    toast.success('پلن با موفقیت حذف شد')

    showDeletePlanModal.value = false
    planToDelete.value = null

    emit('refetch')
  } catch {}
}

const closeDeleteModal = () => {
  if (isDeleting.value) return

  showDeletePlanModal.value = false
  planToDelete.value = null
}
</script>

<template>
    <div>
        <CreatePlanModal v-model="showCreatePlanModal" :service-slug="serviceSlug" :countries="countries"
            :currencies="currencies" @refetch="handlePlanCreate" />

        <UpdatePlanModal v-model="showUpdatePlanModal" :service-slug="serviceSlug" :countries="countries"
            :currencies="currencies" :selected-plan="selectedPlan" @refetch="handlePlanUpdate" />

        <ConfirmModal v-model="showDeletePlanModal" title="حذف پلن" message="آیا از حذف این پلن اطمینان دارید؟"
            confirm-text="بله، حذف شود" cancel-text="بازگشت" :loading="isDeleting" :disabled="isDeleting"
            @confirm="handleDeletePlan" @cancel="closeDeleteModal" />

        <div class="flex items-center justify-between mt-24 mb-10">
            <div class="text-[#1b325a] text-[22px]">
                پلن‌های سرویس
            </div>

            <Button :isAdmin="true" @click="onCreatePlan"
                class="px-4 rounded-[10px] h-10! py-0 text-[13px] font-normal">
                افزودن پلن
            </Button>
        </div>

        <Table :columns="planColumns" :rows="planRows" :actions="planActions" @secondaryAction="onDeletePlan"
            @primaryAction="onEditPlan" />
    </div>
</template>